import { Link } from 'react-router';

/**
 * Docs: the OptiSigns node Agent — the DaemonSet that reports each cluster node's
 * egress IP so infras keeps the MongoDB Atlas IP access lists current. Rendered as
 * a /docs section. Mirrors the real behavior (AgentsController + atlas-whitelist
 * service + inventory-core/agents) so it can't drift far.
 *
 * NB: "Agent" here = the infra DaemonSet (not the AI/MCP agent in Get Started).
 */
export function AgentsDocsPage() {
  return (
    <div className="page">
      <div className="detail-head">
        <h1>Agents</h1>
        <span className="badge">DaemonSet fleet</span>
      </div>
      <p className="detail-desc">
        The <strong>OptiSigns Agent</strong> is a small DaemonSet that runs on every node of our
        Kubernetes clusters. Each pod detects its node&rsquo;s <strong>public egress IP</strong> and
        reports it to infras, which keeps the right <strong>MongoDB Atlas</strong> project IP access
        lists up to date automatically — so cluster egress can always reach Atlas without anyone
        hand-editing whitelists. Migrated from the legacy <code>controller.opstech.org</code>.
        <br />
        <span className="muted">
          Not the AI agent — that&rsquo;s <Link to="/docs/get-started">Get started</Link> /{' '}
          <Link to="/docs/mcp">MCP tools</Link>. This is the node/infra agent.
        </span>
      </p>

      <h3 className="section">How it works</h3>
      <ol className="docs-prose">
        <li>An agent pod boots on a node and detects its <strong>public egress IP</strong>.</li>
        <li>
          It calls <code>POST /api/agents/register</code> on infras with its cluster + node identity
          and IP, authenticated by a <strong>per-cluster token</strong>.
        </li>
        <li>
          infras records the agent in the fleet registry and <strong>pushes the IP to Atlas</strong>,
          then returns the real result so the agent knows it worked.
        </li>
        <li>
          The agent sends <code>POST /api/agents/heartbeat</code> every few minutes so we know it&rsquo;s
          alive.
        </li>
      </ol>

      <h3 className="section">Authentication — per-cluster token</h3>
      <p className="detail-desc">
        Each cluster has its own bearer token (bcrypt-hashed at rest). The agent sends:
      </p>
      <ul className="docs-prose">
        <li><code>ops-cluster-name: &lt;cluster&gt;</code></li>
        <li><code>Authorization: Bearer &lt;cluster token&gt;</code></li>
      </ul>
      <p className="detail-desc">
        Mint / rotate / disable a cluster&rsquo;s token from the <Link to="/agents">Agents</Link> page
        (root or maintainer). Rotating immediately invalidates the old token. A valid token is the
        only gate on being recorded in the registry.
      </p>

      <h3 className="section">Register</h3>
      <ul className="docs-prose">
        <li>
          <strong>Filter-down authz</strong> — an agent may only whitelist into the Atlas projects its
          cluster is mapped to (<code>cluster_atlas_projects</code>). An unauthorized project is
          rejected with <strong>400</strong>. No default-project fallback.
        </li>
        <li>
          <strong>Registry write</strong> — the node is upserted into <code>agents</code> with its IP,
          per-project whitelist state, and a status event (registered / ip_change).
        </li>
        <li>
          <strong>Synchronous Atlas push</strong> — infras adds the IP to the Atlas access list and
          waits for the result, returning a <code>status</code>:
        </li>
      </ul>
      <ul className="docs-prose docs-prose--loose">
        <li><span className="tag-badge">ok</span> IP is live on Atlas (added, or already present).</li>
        <li><span className="tag-badge">rate_limited</span> Atlas returned 429 — the agent backs off and retries.</li>
        <li><span className="tag-badge">failed</span> add failed (or no credentials for the project) — the agent retries.</li>
      </ul>
      <p className="docs-note">
        The agent <strong>retries on any non-success</strong>, so a transient Atlas hiccup self-heals on
        the next attempt.
      </p>

      <h3 className="section">Heartbeat — liveness only</h3>
      <p className="detail-desc">
        Heartbeat does exactly one thing: stamp <code>last_seen_at</code> so we know the agent is alive.
        An unknown agent gets <code>success:false</code> and re-registers. Staleness detection and any
        removal workflow are a separate scheduled <strong>watchdog</strong> (derived from{' '}
        <code>last_seen_at</code>) — never heartbeat itself.
      </p>

      <h3 className="section">The fleet</h3>
      <p className="detail-desc">
        The <Link to="/agents">Agents</Link> page shows every registered node: cluster, node, public IP,
        status, last-seen, and recent lifecycle events — plus the per-cluster token management. Live
        Atlas access lists + reconcile history are on <Link to="/atlas-whitelist">Atlas IPs</Link>.
      </p>

      <h3 className="section">Commands &amp; upgrades</h3>
      <p className="detail-desc">
        The agent also runs a small, <strong>closed set</strong> of cluster operations on request. It is not a
        remote shell: the server can only dispatch an op from this allowlist, and the agent refuses anything
        its own registry doesn&rsquo;t know.
      </p>
      {/* This paragraph used to end "the server never sends an op an agent didn't
          claim". It isn't true: the agent builds a capability list, but the
          register input has no field for it, heartbeat and claim don't carry it,
          and so the capability filter in claimNextCommand is never populated. The
          refusal is real, it just happens on the AGENT after delivery — which is
          a materially different guarantee for anyone reasoning about a
          version-skewed rollout. agent.ts carries the same note at the source. */}
      <p className="docs-note">
        An agent build that doesn&rsquo;t support an op still <strong>receives</strong> it and then refuses
        it — capability advertisement isn&rsquo;t wired through to dispatch yet, so the refusal is the
        agent&rsquo;s, not the server&rsquo;s. During a version-skewed rollout expect failed commands on the
        older nodes rather than commands that were never sent.
      </p>
      <table className="mtx">
        <thead>
          <tr>
            <th>Operation</th>
            <th>Does</th>
            <th>Mutating</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>pod.list</code></td>
            <td>List pods in a namespace (optional label selector)</td>
            <td className="muted">no</td>
          </tr>
          <tr>
            <td><code>workload.status</code></td>
            <td>Status of one DaemonSet / Deployment</td>
            <td className="muted">no</td>
          </tr>
          <tr>
            <td><code>pod.logs</code></td>
            <td>Tail one pod&rsquo;s logs (≤ 1000 lines)</td>
            <td className="muted">no</td>
          </tr>
          <tr>
            <td><code>daemonset.restart</code></td>
            <td>Rolling restart via the <code>restartedAt</code> annotation</td>
            <td>yes</td>
          </tr>
          <tr>
            <td><code>deployment.restart</code></td>
            <td>Rolling restart of a Deployment</td>
            <td>yes</td>
          </tr>
          <tr>
            <td><code>deployment.scale</code></td>
            <td>Set replicas (0–50)</td>
            <td>yes</td>
          </tr>
          <tr>
            <td><code>agent.self_upgrade</code></td>
            <td>Roll the agent DaemonSet to a published release image</td>
            <td>yes</td>
          </tr>
        </tbody>
      </table>

      <h3 className="section">Preview → draft → approve</h3>
      <ol className="docs-prose">
        <li>
          <strong>Preview</strong> (<code>agentCommandPlan</code>) renders the summary, the diff and any
          warnings. It persists nothing.
        </li>
        <li>
          <strong>Draft</strong> writes the command with that plan <em>frozen</em> onto the row — including
          server-generated values such as the restart annotation&rsquo;s <code>restartedAt</code> — plus a{' '}
          <strong>plan digest</strong>: a hash of everything the human was shown.
        </li>
        <li>
          <strong>Approve</strong> echoes the draft&rsquo;s digest back. The server recomputes the plan and
          refuses if it no longer matches, so &ldquo;never apply without reading the diff&rdquo; is enforced by
          machine rather than by discipline. If the world moved (a cluster flipped to production, a workload
          changed), you get the new diff instead of a surprise rollout.
        </li>
      </ol>
      <p className="docs-note">
        Approval is the <strong>only</strong> path to a dispatchable command — there is no enqueue, and a DB
        constraint makes an unapproved dispatchable row impossible. Because the draft freezes the plan, always
        approve with the <em>draft&rsquo;s own</em> digest: a fresh preview of identical inputs legitimately
        hashes differently, and the server will (correctly) refuse it.
      </p>
      <ul className="docs-prose">
        <li>
          <strong>Mutating + production = root only.</strong> A maintainer can draft it — the UI button reads
          &ldquo;Request root approval&rdquo; — but only a root can approve it. The UI also makes you type the
          cluster name first.
        </li>
        <li>
          <strong>Output is separately gated.</strong> Command output can carry pod logs, so it is not a field
          on the command and is fetched only when you click <em>Load output</em> (root or maintainer).
        </li>
        <li>
          Every state change is an append-only event on the command — who drafted, who approved, what the
          agent reported.
        </li>
      </ul>

      <h3 className="section">Four switches, in different places</h3>
      <p className="detail-desc">
        A command only runs when <strong>all four</strong> of these are true. They are not in one place,
        which is why a cluster you just enabled can still look inert:
      </p>
      <ol className="docs-prose">
        <li>
          <strong>Leader election</strong> — <code>AGENT_LEADER_ELECTION_ENABLED</code> in the cluster&rsquo;s{' '}
          <code>optisigns-agent-config</code> ConfigMap. Only the Lease holder polls, so with this off the
          command loop never starts, whatever else is enabled.
        </li>
        <li>
          <strong>Server, per cluster</strong> — <code>commands_enabled</code> (plus{' '}
          <code>is_production</code>, an optional namespace allowlist and an op allowlist). Off unless a
          settings row says otherwise. A root flips it by clicking the{' '}
          <code>commands on</code>/<code>commands off</code> pill on the{' '}
          <Link to="/agents">Agents</Link> page. Enrolling agents never implicitly grants the command plane.
        </li>
        <li>
          <strong>The cluster&rsquo;s agents</strong> — <code>AGENT_COMMANDS_ENABLED</code> in the same
          ConfigMap, which needs a DaemonSet rollout to take effect.
        </li>
        <li>
          <strong>The op must be one the agent&rsquo;s own flags enable</strong> — and this rung is enforced{' '}
          <strong>on the agent, not by the server</strong>. With <code>AGENT_MUTATING_OPS_ENABLED</code> and{' '}
          <code>AGENT_POD_LOGS_ENABLED</code> still false an agent enables exactly <code>pod.list</code> and{' '}
          <code>workload.status</code>, but dispatch is not capability-filtered: a non-enabled op is
          delivered and then refused by the agent, so it burns its attempts and ends{' '}
          <code>failed</code> rather than never being sent. The <strong>Pods</strong> action is the entry
          point for this read-only rung; <strong>Logs</strong> and <strong>Restart</strong> grey out only
          once an agent actually reports <code>capabilities</code>, and <strong>Upgrade</strong> never
          consults the agent&rsquo;s flags at all.
        </li>
      </ol>
      <p className="detail-desc">
        Roll them in order — lease, then commands, then mutating — one cluster at a time. And{' '}
        <code>agent-command-reaper</code> must be running wherever commands are: only one mutating command
        may be in flight per cluster, so a single crashed executor with no reaper wedges that cluster&rsquo;s
        channel permanently.
      </p>
      <p className="detail-desc">
        The <strong>desired version</strong> comes from the same row (falling back to the default published
        release). The Agents page shows <code>onDesired/total</code> per cluster; the{' '}
        <strong>Upgrade</strong> button drafts an <code>agent.self_upgrade</code> and disappears once the whole
        cluster is on the desired version.
      </p>

      <h3 className="section">What the agent may do in your cluster</h3>
      <p className="detail-desc">
        Since 2026-07-27 the agent&rsquo;s ServiceAccount carries a <strong>ClusterRole</strong>, not the
        namespaced Role it started with. Per-namespace Roles cannot see a namespace created after the
        fact, which makes a cluster-wide workload inventory impossible to keep correct.
      </p>
      <ul className="docs-prose">
        <li>
          <strong>Read</strong> (<code>get</code>, <code>list</code>, <code>watch</code>) — pods, services,
          namespaces; deployments, daemonsets, statefulsets, replicasets; jobs, cronjobs.
        </li>
        <li>
          <strong>Write</strong> (<code>patch</code> only) — deployments, daemonsets,{' '}
          <code>deployments/scale</code>. Nothing else, and never <code>create</code> or{' '}
          <code>delete</code>.
        </li>
        <li>
          <strong>Never, at any scope</strong> — <code>secrets</code>, <code>configmaps</code>,{' '}
          <code>pods/exec</code>, <code>pods/portforward</code>, and{' '}
          <code>roles</code>/<code>rolebindings</code>, so the agent cannot widen its own grant.{' '}
          <code>pods/log</code> stays a separate per-namespace opt-in.
        </li>
      </ul>
      <p className="detail-desc">
        You do not have to take that list on trust. Every agent runs a{' '}
        <code>SelfSubjectAccessReview</code> at boot and prints an ALLOW/DENY table to its log,
        including a <code>get secrets</code> probe labelled <code>MUST BE DENIED</code>.
      </p>
      <div className="dep-card docs-tip">
        <strong>The cost of that widening, stated plainly:</strong> <code>patch</code> on a workload is
        patch on its <em>whole spec</em> — image, command, env and <code>serviceAccountName</code> included
        — and it is now bounded by nothing in RBAC. RBAC also binds to the ServiceAccount, not the pod, so
        every agent pod on the cluster holds these rights, not just the leader. What actually bounds a
        mutating command is <code>AGENT_MUTATING_OPS_ENABLED</code> (false everywhere today), the{' '}
        <code>AGENT_ALLOWED_NAMESPACES</code> check the agent applies in code on every call, and
        server-side approval. If you need more reach, widen the namespace list — not the ClusterRole.
      </div>

      <h3 className="section">Leadership is observed, not assigned</h3>
      <p className="detail-desc">
        One agent per cluster holds a Kubernetes <strong>Lease</strong> and simply <em>reports</em> that it is
        the holder; infras records it as display metadata (<code>is_leader</code>,{' '}
        <code>leader_holder_identity</code>, <code>leader_observed_at</code>) and a watchdog clears the flag
        when the report goes stale. The server <strong>never elects a leader</strong> — k8s does. A
        cluster-targeted command is picked up by whichever agent is holding the lease at claim time, which is
        why a cluster with no current leader shows a warning on the plan.
      </p>

      <h3 className="section">Onboarding a cluster</h3>
      <ol className="docs-prose">
        <li>Map the cluster to its Atlas project(s) (<code>cluster_atlas_projects</code>).</li>
        <li>Ensure the Atlas project has OAuth credentials in the infras secret bundle.</li>
        <li><Link to="/agents">Mint a token</Link> for the cluster.</li>
        <li>
          Deploy (or repoint) the DaemonSet with <code>CONTROLLER_URL</code> = the infras API host and
          the minted <code>CLUSTER_TOKEN</code>. That&rsquo;s the whole cutover — the agent binary is
          unchanged.
        </li>
      </ol>

      <h3 className="section">What gates it — no feature flags</h3>
      <ul className="docs-prose">
        <li><strong>Registry</strong> is written whenever a request carries a valid cluster token.</li>
        <li>
          <strong>Atlas management</strong> is always on; the only gate is whether the project has
          credentials. To disable a project, set <code>atlas_projects.is_active = false</code> or remove
          its credentials.
        </li>
      </ul>

      <div className="dep-card docs-tip">
        <strong>Tip:</strong> an agent pod stuck in CrashLoopBackOff almost always means a bad/rotated
        <code> CLUSTER_TOKEN</code> or a wrong <code>CONTROLLER_URL</code> — check the pod logs and the{' '}
        <Link to="/agents">Agents</Link> page.
      </div>
    </div>
  );
}
